// VNEC v9 — pages/pyctt.js
// Hồ sơ Thanh toán — 3 loại: thanh_toan / tam_ung / ung_quy
// Port từ v8 FormTaoPhieuYCTT (1558 dòng) sang Supabase.
//
// Luồng:
//   Thanh toán:
//     PHẦN 1 (cost section): chọn HĐ + nhập cost rows (noi_dung, khoi_luong, don_gia, loai_cp, phan_loai...)
//     PHẦN 2 (payment): VAT%, Giữ QT%, Giữ BH%, Trừ TU, Trừ Khác → calc Chi trong kỳ
//     Save → INSERT pyctt header + INSERT chi_phi rows với ma_chi_tiet = so_pyctt
//   Tạm ứng:
//     Chọn HĐ đã approved → nhập số tiền + VAT → Save pyctt header (không có cost rows)
//   Ứng quỹ:
//     Chọn dự án + HĐ (tùy chọn) + nội dung → nhập số tiền → Save pyctt header
//
// Công thức calc:
//   Thanh toán: vat = tt*vatPct/100; sauVat = tt+vat; giuQT = sauVat*giuQTPct/100; giuBH = sauVat*giuBHPct/100;
//               chiTrongKy = sauVat - giuQT - giuBH - truTU - truKhac
//   Tạm ứng:    vat = tt*vatPct/100; sauVat = tt+vat; chiTrongKy = sauVat
//   Ứng quỹ:    chiTrongKy = tt (không VAT, không giữ)

import { supabase, submitForApproval, checkBudget } from '../lib/supabase.js';
import { formatVND, parseVND, stateLabel, attachVNDFormat, esc } from '../lib/forms.js';
import { printPYC } from '../lib/pdf-export.js';
import { ok, err } from '../components/toast.js';

// ═══════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════
export async function renderPycttList() {
  window.__pageInit = initList;
  return `
    <div class="page">
      <div class="page-header">
        <h1>Phiếu Yêu Cầu Thanh Toán</h1>
        <div class="page-actions">
          <input type="search" id="search" placeholder="Tìm số phiếu, HĐ, nhà thầu..." class="input-search">
          <select id="filter-loai" class="input-sm">
            <option value="">Tất cả loại</option>
            <option value="thanh_toan">Thanh toán</option>
            <option value="tam_ung">Tạm ứng</option>
            <option value="ung_quy">Ứng quỹ</option>
          </select>
          <select id="filter-state" class="input-sm">
            <option value="">Tất cả trạng thái</option>
            <option value="draft">Nháp</option>
            <option value="pending_l1">Chờ duyệt L1</option>
            <option value="pending_l2">Chờ duyệt L2</option>
            <option value="approved">Đã duyệt</option>
            <option value="rejected">Từ chối</option>
          </select>
          <a href="#/pyctt/new" class="btn btn-primary">+ Tạo phiếu</a>
        </div>
      </div>
      <div class="card">
        <div class="table-wrap">
          <table class="table" id="pyctt-table">
            <thead><tr>
              <th>Số phiếu</th><th>Loại</th><th>Mã HĐ</th><th>Nhà thầu</th>
              <th>Kỳ TT</th><th class="num">Số tiền</th><th class="num">Chi trong kỳ</th>
              <th>Trạng thái</th><th></th>
            </tr></thead>
            <tbody><tr><td colspan="9" class="muted center">Đang tải...</td></tr></tbody>
          </table>
        </div>
      </div>
    </div>`;
}

async function initList() {
  const search = document.getElementById('search');
  const fLoai = document.getElementById('filter-loai');
  const fState = document.getElementById('filter-state');
  const tbody = document.querySelector('#pyctt-table tbody');

  async function query() {
    tbody.innerHTML = '<tr><td colspan="9" class="muted center">Đang tải...</td></tr>';
    let q = supabase.from('pyctt')
      .select('id, so_pyctt, loai_pyc, ma_hd, nguoi_thu_huong, ky_tt, so_tien, chi_trong_ky, trang_thai')
      .is('deleted_at', null).order('created_at', { ascending: false }).limit(500);
    if (fLoai.value) q = q.eq('loai_pyc', fLoai.value);
    if (fState.value) q = q.eq('trang_thai', fState.value);
    if (search.value) q = q.or(`so_pyctt.ilike.%${search.value}%,ma_hd.ilike.%${search.value}%,nguoi_thu_huong.ilike.%${search.value}%`);
    const { data, error } = await q;
    if (error) { tbody.innerHTML = `<tr><td colspan="9">Lỗi: ${error.message}</td></tr>`; return; }
    if (!data.length) { tbody.innerHTML = '<tr><td colspan="9" class="muted center">Chưa có phiếu nào.</td></tr>'; return; }

    const loaiLabel = { thanh_toan: '💰 TT', tam_ung: '💳 TƯ', ung_quy: '🏧 UQ' };
    tbody.innerHTML = data.map(r => `
      <tr>
        <td><b>${esc(r.so_pyctt || '')}</b></td>
        <td class="small">${loaiLabel[r.loai_pyc] || r.loai_pyc}</td>
        <td class="small">${esc(r.ma_hd || '')}</td>
        <td class="small">${esc(r.nguoi_thu_huong || '')}</td>
        <td class="small">${esc(r.ky_tt || '')}</td>
        <td class="num">${formatVND(r.so_tien)}</td>
        <td class="num bold">${formatVND(r.chi_trong_ky)}</td>
        <td>${stateLabel(r.trang_thai)}</td>
        <td><a href="#/pyctt/${r.id}" class="btn btn-ghost btn-sm">Xem</a> <button class="btn btn-ghost btn-sm" data-print-id="${r.id}" title="In / Xuất PDF">🖨</button></td>
      </tr>`).join('');

    // Print button handlers
    tbody.querySelectorAll('button[data-print-id]').forEach(btn => {
      btn.addEventListener('click', () => printPYC(btn.dataset.printId));
    });
  }

  let t;
  search.addEventListener('input', () => { clearTimeout(t); t = setTimeout(query, 300); });
  fLoai.addEventListener('change', query);
  fState.addEventListener('change', query);
  query();
}

// ═══════════════════════════════════════════════════════════
// FORM — 3 types
// ═══════════════════════════════════════════════════════════
export async function renderPycttForm(id) {
  window.__pageInit = () => initForm(id);
  return `
    <div class="page">
      <div class="page-header">
        <h1 id="form-title">${id ? 'Chỉnh sửa phiếu' : 'Tạo hồ sơ thanh toán'}</h1>
        <a href="#/pyctt" class="btn btn-ghost">← Danh sách</a>
      </div>

      <!-- PYC Type selector -->
      <div class="pyc-type-bar" id="pyc-type-bar" style="display:flex;gap:0;margin-bottom:16px;border-radius:8px;overflow:hidden;border:1px solid #DDD">
        <button type="button" class="pyc-type-btn active-tt" data-type="thanh_toan" style="flex:1;padding:14px 8px;border:none;border-right:1px solid #DDD;background:#EEEDFE;color:#534AB7;font-weight:700;cursor:pointer;border-bottom:3px solid #534AB7">
          <div style="font-size:18px">💰</div>Thanh toán
          <div style="font-size:10px;font-weight:400;color:#888;margin-top:2px">Chi phí + lệnh chi</div>
        </button>
        <button type="button" class="pyc-type-btn" data-type="tam_ung" style="flex:1;padding:14px 8px;border:none;border-right:1px solid #DDD;background:#FFF;color:#888;font-weight:700;cursor:pointer">
          <div style="font-size:18px">💳</div>Tạm ứng
          <div style="font-size:10px;font-weight:400;color:#888;margin-top:2px">Ứng trước nhà thầu</div>
        </button>
        <button type="button" class="pyc-type-btn" data-type="ung_quy" style="flex:1;padding:14px 8px;border:none;background:#FFF;color:#888;font-weight:700;cursor:pointer">
          <div style="font-size:18px">🏧</div>Ứng quỹ
          <div style="font-size:10px;font-weight:400;color:#888;margin-top:2px">Chi tiền mặt</div>
        </button>
      </div>

      <!-- ═════ PHẦN 1 (chỉ hiện khi thanh_toan): Chi tiết Chi phí ═════ -->
      <div id="cost-section" style="display:block">
        <div class="section-label" style="font-size:13px;font-weight:800;color:#000066;margin-bottom:10px">📋 PHẦN 1: Chi tiết Chi phí Nghiệm thu</div>

        <div class="card">
          <h3 class="card-title">Thông tin chung</h3>
          <div class="grid-2">
            <label class="field"><span>Dự án</span>
              <select id="cs-duan"><option value="">-- Tất cả dự án --</option></select>
            </label>
            <label class="field"><span>Chọn HĐ gốc</span>
              <select id="cs-hd">
                <option value="">-- Chọn HĐ --</option>
                <option value="__VOUCHER__" style="color:#854F0B;font-weight:700">📄 Chi phí độc lập (Voucher)</option>
              </select>
            </label>
            <label class="field"><span>Đợt</span>
              <div style="display:flex;gap:6px;align-items:center">
                <input id="cs-dot" readonly style="flex:1" placeholder="Tự động">
                <label style="display:flex;gap:4px;align-items:center;white-space:nowrap;font-size:11px">
                  <input type="checkbox" id="cs-qt"> Quyết toán
                </label>
              </div>
            </label>
            <label class="field"><span>Nhà thầu</span>
              <input id="cs-ncc" readonly>
            </label>
            <label class="field"><span>Mã chi phí</span>
              <input id="cs-macp" readonly placeholder="Tự động" style="font-weight:700;color:#000066">
            </label>
            <label class="field" id="cs-voucher-maduan" style="display:none"><span>Mã dự án (Voucher) *</span>
              <input id="cs-inp-maduan" placeholder="VD: DA-001">
            </label>
          </div>
        </div>

        <!-- Budget guard -->
        <div class="card" id="budget-guard" style="display:none;border-left:4px solid var(--green,#1B6B3A)">
          <div style="display:flex;justify-content:space-between;margin-bottom:8px">
            <b>🛡 Ngân sách HĐ</b>
            <b id="bg-pct" style="font-size:16px">0%</b>
          </div>
          <div style="height:14px;background:#ECEDFE;border-radius:8px;overflow:hidden">
            <div id="bg-fill" style="height:100%;width:0%;background:#1B6B3A;transition:width .5s"></div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-top:10px;font-size:11px">
            <div style="text-align:center;padding:6px;background:#F4F4FB;border-radius:6px">
              <div style="font-weight:800" id="bg-hd">0</div>
              <div style="color:#666">Giá trị HĐ</div>
            </div>
            <div style="text-align:center;padding:6px;background:#F4F4FB;border-radius:6px">
              <div style="font-weight:800" id="bg-cp">0</div>
              <div style="color:#666">CP lũy kế</div>
            </div>
            <div style="text-align:center;padding:6px;background:#F4F4FB;border-radius:6px" id="bg-remain-item">
              <div style="font-weight:800" id="bg-remain">0</div>
              <div style="color:#666">Còn lại</div>
            </div>
          </div>
        </div>

        <!-- Import box from hop_dong_items -->
        <div class="card" id="import-box" style="display:none;background:linear-gradient(135deg,#FFF8E1 0%,#FEF3C7 100%);border-left:4px solid #EF9F27">
          <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
            <div style="flex:1;min-width:200px">
              <div style="font-size:13px;font-weight:800;color:#854F0B">📦 Nhập nhanh từ danh mục HĐ</div>
              <div style="font-size:11px;color:#92400E;margin-top:2px">Tự động tạo các dòng chi phí từ hạng mục của hợp đồng đã chọn</div>
            </div>
            <button type="button" id="btn-import-hd"
              style="background:#000066;color:#FFCC00;border:1px solid #000044;border-radius:6px;padding:10px 22px;font-family:inherit;font-size:12px;font-weight:700;cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,0.15);transition:all .15s"
              disabled>
              <span id="import-btn-icon">⏳</span>
              <span id="import-btn-label">Đang tải danh mục...</span>
            </button>
            <span id="import-chip" style="font-size:10px;font-weight:700;padding:4px 10px;border-radius:12px;background:#F4F4FB;color:#666">⏳ Đang tải...</span>
          </div>
        </div>

        <div class="card">
          <h3 class="card-title">Chi tiết chi phí <button type="button" class="btn btn-ghost btn-sm" id="btn-add-row" style="float:right">+ Thêm dòng</button></h3>
          <div class="table-wrap">
            <table class="table table-compact" id="cost-table" style="table-layout:fixed;width:1080px">
              <colgroup>
                <col style="width:32px">
                <col style="width:320px">
                <col style="width:55px">
                <col style="width:85px">
                <col style="width:105px">
                <col style="width:115px">
                <col style="width:120px">
                <col style="width:110px">
                <col style="width:60px">
                <col style="width:100px">
                <col style="width:38px">
              </colgroup>
              <thead><tr>
                <th>#</th>
                <th>Nội dung *</th>
                <th>ĐV</th>
                <th>KL</th>
                <th>Đơn giá</th>
                <th>Thành tiền</th>
                <th>Loại CP *</th>
                <th>IFRS</th>
                <th title="Số tháng phân bổ — chỉ áp dụng cho Prepaid">Tháng PB</th>
                <th>Mã VT</th>
                <th></th>
              </tr></thead>
              <tbody id="cost-tbody"></tbody>
            </table>
          </div>
          <div style="text-align:right;margin-top:10px;font-size:14px;font-weight:800">
            Tổng: <span id="cost-total" style="color:#1B6B3A">0</span> VNĐ
          </div>
        </div>
      </div>

      <!-- Tạm ứng: chọn HĐ -->
      <div class="card" id="tu-lookup" style="display:none;border-left:4px solid #185FA5">
        <h3 class="card-title" style="color:#185FA5">Tạm ứng — Chọn Hợp đồng</h3>
        <div class="grid-2">
          <label class="field"><span>Dự án</span>
            <select id="tu-duan"><option value="">-- Tất cả --</option></select>
          </label>
          <label class="field"><span>Hợp đồng (approved) *</span>
            <select id="tu-hd"><option value="">-- Chọn HĐ --</option></select>
          </label>
          <label class="field"><span>Giá trị HĐ</span>
            <input id="tu-giatri" readonly style="text-align:right;color:#1B6B3A;font-weight:700">
          </label>
        </div>
      </div>

      <!-- Ứng quỹ: nhập nội dung -->
      <div class="card" id="uq-lookup" style="display:none;border-left:4px solid #854F0B">
        <h3 class="card-title" style="color:#854F0B">Ứng quỹ — Chi tiền mặt</h3>
        <div class="grid-2">
          <label class="field"><span>Dự án</span>
            <select id="uq-duan"><option value="">-- Tất cả --</option></select>
          </label>
          <label class="field"><span>Gắn HĐ (tùy chọn)</span>
            <select id="uq-hd"><option value="">-- Không gắn HĐ --</option></select>
          </label>
        </div>
        <label class="field"><span>Nội dung *</span>
          <input id="uq-noidung" placeholder="VD: Chi phí tiếp khách, mua VPP...">
        </label>
      </div>

      <hr class="hstt-divider" style="height:2px;background:linear-gradient(90deg,#000066,#FFCC00,#000066);border:none;margin:20px 0;border-radius:2px">

      <!-- ═════ PHẦN 2: Thông tin Thanh toán ═════ -->
      <div class="section-label" style="font-size:13px;font-weight:800;color:#000066;margin-bottom:10px">💰 PHẦN 2: Thông tin Thanh toán</div>

      <div class="card" id="card-info">
        <h3 class="card-title">Thông tin hồ sơ</h3>
        <div class="grid-2">
          <label class="field"><span>Số phiếu</span>
            <input id="inp-sophieu" readonly placeholder="Tự động">
          </label>
          <label class="field"><span>Mã hợp đồng</span>
            <input id="inp-mahd" readonly>
          </label>
          <label class="field"><span>Người thụ hưởng</span>
            <input id="inp-nthhuong">
          </label>
          <label class="field"><span>Kỳ thanh toán *</span>
            <input id="inp-kytt" placeholder="VD: Đợt 1 Tháng 3/2026" required>
          </label>
          <label class="field"><span>Thành tiền</span>
            <input id="inp-thanhtien" type="number" readonly>
          </label>
        </div>
      </div>

      <div class="card" id="card-calc">
        <h3 class="card-title">Tính toán thanh toán</h3>
        <div class="grid-2">
          <label class="field" id="fld-vat"><span>VAT (%)</span>
            <input id="inp-vat-pct" type="number" value="10" step="0.1" min="0" max="100">
          </label>
          <label class="field" id="fld-giuqt"><span>Giữ quyết toán (%)</span>
            <input id="inp-giuqt-pct" type="number" value="5" step="0.1" min="0" max="100">
          </label>
          <label class="field" id="fld-giubh"><span>Giữ bảo hành (%)</span>
            <input id="inp-giubh-pct" type="number" value="5" step="0.1" min="0" max="100">
          </label>
          <label class="field" id="fld-trutu"><span>Trừ tạm ứng (VNĐ)</span>
            <input id="inp-trutu" type="number" value="0">
          </label>
          <label class="field" id="fld-trukhac"><span>Trừ khác (VNĐ)</span>
            <input id="inp-trukhac" type="number" value="0">
          </label>
        </div>
        <div class="calc-grid" style="display:grid;grid-template-columns:repeat(6,1fr);gap:10px;margin-top:16px">
          <div class="calc-card" style="background:#F4F4FB;padding:12px;border-radius:8px;text-align:center">
            <div style="font-size:16px;font-weight:800" id="d-thanhtien">0</div>
            <div style="font-size:10px;color:#666">Thành tiền</div>
          </div>
          <div class="calc-card" id="card-vat" style="background:#F4F4FB;padding:12px;border-radius:8px;text-align:center">
            <div style="font-size:16px;font-weight:800" id="d-vat">0</div>
            <div style="font-size:10px;color:#666">VAT</div>
          </div>
          <div class="calc-card" style="background:#E6F1FB;padding:12px;border-radius:8px;text-align:center;border:1px solid #185FA5">
            <div style="font-size:16px;font-weight:800;color:#185FA5" id="d-sauvat">0</div>
            <div style="font-size:10px;color:#185FA5">Sau VAT</div>
          </div>
          <div class="calc-card" id="card-giuqt" style="background:#FFF8E1;padding:12px;border-radius:8px;text-align:center">
            <div style="font-size:16px;font-weight:800;color:#854F0B" id="d-giuqt">0</div>
            <div style="font-size:10px;color:#854F0B">Giữ QT</div>
          </div>
          <div class="calc-card" id="card-giubh" style="background:#FFF8E1;padding:12px;border-radius:8px;text-align:center">
            <div style="font-size:16px;font-weight:800;color:#854F0B" id="d-giubh">0</div>
            <div style="font-size:10px;color:#854F0B">Giữ BH</div>
          </div>
          <div class="calc-card" id="card-result" style="background:#E8F5EE;padding:12px;border-radius:8px;text-align:center;border:2px solid #1B6B3A">
            <div style="font-size:18px;font-weight:800;color:#1B6B3A" id="d-chitrongky">0</div>
            <div style="font-size:10px;color:#1B6B3A;font-weight:700">CHI TRONG KỲ</div>
          </div>
        </div>
      </div>

      <div class="card">
        <h3 class="card-title">Hình thức thanh toán</h3>
        <div style="display:flex;gap:10px">
          <div class="httt-opt selected" id="httt-tk" data-v="tai_khoan" style="flex:1;padding:14px;border:2px solid #000066;border-radius:8px;cursor:pointer;text-align:center;background:#000066;color:#FFCC00;font-weight:700">
            🏧 Chuyển khoản
          </div>
          <div class="httt-opt" id="httt-tm" data-v="tien_mat" style="flex:1;padding:14px;border:2px solid #DDD;border-radius:8px;cursor:pointer;text-align:center;font-weight:700">
            💵 Tiền mặt
          </div>
        </div>
        <div id="cash-warn" style="display:none;margin-top:10px;padding:10px;background:#FFF8E1;border:1px solid #D97706;border-radius:6px;font-size:11px;color:#854F0B"></div>
      </div>

      <div class="form-actions">
        <a href="#/pyctt" class="btn btn-ghost">Hủy</a>
        <button type="button" class="btn btn-primary" id="btn-save">Lưu nháp</button>
        <button type="button" class="btn btn-success" id="btn-submit">Lưu & Gửi duyệt</button>
      </div>
    </div>`;
}

// ─── State ───
let _state = {
  loaiPYC: 'thanh_toan',
  editId: null,
  editData: null,
  hinhThucTT: 'tai_khoan',
  loaiCPList: [],
  ifrsRules: {},
  vatTuList: [],
  allHD: [],
  budget: null,
  rowCount: 0,
  cashThreshold: 20000000,
  calc: {},  // { vat, sauVat, giuQT, giuBH, truTU, truKhac, chiTrongKy }
  // v3.1: import items from hop_dong_items
  itemsCache: {},      // { hop_dong_id: { items: [...], fetchedAt } }
  itemsCacheTTL: 5 * 60 * 1000  // 5 min
};

async function initForm(id) {
  _state.editId = id || null;
  _state.rowCount = 0;

  // Load catalogs
  const [rulesRes, vtRes, daRes, hdRes] = await Promise.all([
    supabase.from('ifrs_rules').select('loai_cp, default_phan_loai, locked'),
    supabase.from('vat_tu_catalog').select('ma_vat_tu, ten_vat_tu, don_vi').eq('is_active', true),
    supabase.from('du_an').select('ma_da, ten_da').eq('trang_thai', 'active').is('deleted_at', null).order('ma_da'),
    supabase.from('hop_dong').select('id, so_hd, ma_hd, nha_thau, ma_du_an, gia_tri, phan_loai_hd')
      .eq('trang_thai', 'approved').is('deleted_at', null).limit(2000)
  ]);

  _state.loaiCPList = (rulesRes.data || []).map(r => r.loai_cp);
  _state.ifrsRules = Object.fromEntries((rulesRes.data || []).map(r => [r.loai_cp, r]));
  _state.vatTuList = vtRes.data || [];
  _state.allHD = (hdRes.data || []).map(h => ({
    id: h.id,
    ma: h.ma_hd || h.so_hd,
    ncc: h.nha_thau,
    maDA: h.ma_du_an,
    giaTri: h.gia_tri,
    phanLoai: h.phan_loai_hd
  }));

  // Populate dự án dropdowns
  const daList = daRes.data || [];
  ['cs-duan', 'tu-duan', 'uq-duan'].forEach(id => {
    const sel = document.getElementById(id); if (!sel) return;
    daList.forEach(d => sel.insertAdjacentHTML('beforeend', `<option value="${d.ma_da}">${d.ma_da} — ${esc(d.ten_da)}</option>`));
  });

  populateHDSelect('cs-hd', _state.allHD, true);
  populateHDSelect('tu-hd', _state.allHD, false);
  populateHDSelect('uq-hd', _state.allHD, false);

  // v3: Auto-fill + lock project cho data_entry
  try {
    const { getCurrentProject } = await import('../lib/session.js');
    const { getCurrentUser } = await import('../lib/supabase.js');
    const u = await getCurrentUser();
    const curProj = await getCurrentProject();
    if (curProj) {
      ['cs-duan', 'tu-duan', 'uq-duan'].forEach(id => {
        const sel = document.getElementById(id); if (!sel) return;
        sel.value = curProj;
        if (u.role === 'data_entry' || u.role === 'viewer') {
          sel.disabled = true;
          sel.style.background = '#EEEDFE';
          sel.title = 'Dự án khóa theo phiên làm việc';
        }
      });
      // Filter HĐ dropdown theo dự án
      const filtered = _state.allHD.filter(h => h.maDA === curProj);
      populateHDSelect('cs-hd', filtered, true);
      populateHDSelect('tu-hd', filtered, false);
      populateHDSelect('uq-hd', filtered, false);
    }
  } catch (e) { /* silent */ }

  // Wire type switcher
  document.querySelectorAll('.pyc-type-btn').forEach(btn => {
    btn.addEventListener('click', () => switchType(btn.dataset.type));
  });

  // Wire cost section handlers
  document.getElementById('cs-duan').addEventListener('change', onCSDuAnChange);
  document.getElementById('cs-hd').addEventListener('change', onCSHDChange);
  document.getElementById('cs-inp-maduan').addEventListener('input', updateMaCP);
  document.getElementById('btn-add-row').addEventListener('click', () => addRow());
  document.getElementById('btn-import-hd').addEventListener('click', importItemsFromHD);

  // Tạm ứng handlers
  document.getElementById('tu-duan').addEventListener('change', () => {
    const da = document.getElementById('tu-duan').value;
    populateHDSelect('tu-hd', da ? _state.allHD.filter(h => h.maDA === da) : _state.allHD, false);
  });
  document.getElementById('tu-hd').addEventListener('change', onTUHDChange);

  // Ứng quỹ handlers
  document.getElementById('uq-duan').addEventListener('change', () => {
    const da = document.getElementById('uq-duan').value;
    populateHDSelect('uq-hd', da ? _state.allHD.filter(h => h.maDA === da) : _state.allHD, false);
  });

  // Payment calc handlers
  ['inp-vat-pct', 'inp-giuqt-pct', 'inp-giubh-pct', 'inp-trutu', 'inp-trukhac', 'inp-thanhtien'].forEach(id => {
    document.getElementById(id).addEventListener('input', calcPYC);
  });

  // HTTT handlers
  document.getElementById('httt-tk').addEventListener('click', () => setHTTT('tai_khoan'));
  document.getElementById('httt-tm').addEventListener('click', () => setHTTT('tien_mat'));

  // Save handlers
  document.getElementById('btn-save').addEventListener('click', () => savePYC(false));
  document.getElementById('btn-submit').addEventListener('click', () => savePYC(true));

  // Edit mode: load existing
  if (_state.editId) {
    await loadEditData(_state.editId);
  } else {
    switchType('thanh_toan');
    addRow(); addRow();

    // Pre-fill parent HD from quick action on hop-dong list
    const prefillHD = sessionStorage.getItem('vnec_prefill_parent_pyc');
    if (prefillHD) {
      sessionStorage.removeItem('vnec_prefill_parent_pyc');
      // Poll for the option to appear (HD list loads async + filtered by project)
      const pollUntil = Date.now() + 3000;
      const tryFill = () => {
        // Try all 3 type tabs - cs-hd (Thanh toán), tu-hd (Tạm ứng), uq-hd (Ứng quỹ)
        const sels = ['cs-hd', 'tu-hd', 'uq-hd'].map(id => document.getElementById(id)).filter(Boolean);
        const sel = sels.find(s => [...s.options].some(o => o.value === prefillHD));
        if (sel) {
          sel.value = prefillHD;
          sel.dispatchEvent(new Event('change'));
          ok(`Đã chọn HĐ: ${prefillHD}`);
        } else if (Date.now() < pollUntil) {
          setTimeout(tryFill, 100);
        } else {
          err(`HĐ "${prefillHD}" không có trong dropdown. Có thể: chưa duyệt, hoặc khác dự án hiện tại, hoặc bạn không có quyền.`);
        }
      };
      tryFill();
    }
  }
}

// ─── HD select population ───
function populateHDSelect(selId, hdList, includeVoucher) {
  const sel = document.getElementById(selId); if (!sel) return;
  let html = '<option value="">-- Chọn HĐ --</option>';
  if (includeVoucher) html += '<option value="__VOUCHER__" style="color:#854F0B;font-weight:700">📄 Chi phí độc lập (Voucher)</option>';
  hdList.forEach(h => { html += `<option value="${esc(h.ma)}">${esc(h.ma)} — ${esc(h.ncc || '')}</option>`; });
  sel.innerHTML = html;
}

// ─── Type switcher ───
function switchType(type) {
  _state.loaiPYC = type;
  document.querySelectorAll('.pyc-type-btn').forEach(b => {
    const active = b.dataset.type === type;
    if (active) {
      const cls = { thanh_toan: ['#EEEDFE','#534AB7'], tam_ung: ['#E6F1FB','#185FA5'], ung_quy: ['#FAEEDA','#854F0B'] }[type];
      b.style.background = cls[0]; b.style.color = cls[1]; b.style.borderBottom = '3px solid ' + cls[1];
    } else {
      b.style.background = '#FFF'; b.style.color = '#888'; b.style.borderBottom = 'none';
    }
  });
  document.getElementById('cost-section').style.display = type === 'thanh_toan' ? 'block' : 'none';
  document.getElementById('tu-lookup').style.display = type === 'tam_ung' ? 'block' : 'none';
  document.getElementById('uq-lookup').style.display = type === 'ung_quy' ? 'block' : 'none';

  // Show/hide calc fields
  const showGiu = type === 'thanh_toan';
  const showVAT = type !== 'ung_quy';
  const showTru = type === 'thanh_toan';
  document.getElementById('fld-giuqt').style.display = showGiu ? '' : 'none';
  document.getElementById('fld-giubh').style.display = showGiu ? '' : 'none';
  document.getElementById('fld-vat').style.display = showVAT ? '' : 'none';
  document.getElementById('fld-trutu').style.display = showTru ? '' : 'none';
  document.getElementById('fld-trukhac').style.display = showTru ? '' : 'none';
  document.getElementById('card-giuqt').style.display = showGiu ? '' : 'none';
  document.getElementById('card-giubh').style.display = showGiu ? '' : 'none';
  document.getElementById('card-vat').style.display = showVAT ? '' : 'none';

  // Thành tiền readonly cho thanh_toan (sync từ cost table)
  document.getElementById('inp-thanhtien').readOnly = (type === 'thanh_toan');

  // Ứng quỹ: force tiền mặt
  if (type === 'ung_quy') {
    setHTTT('tien_mat');
    document.getElementById('httt-tk').style.display = 'none';
  } else {
    document.getElementById('httt-tk').style.display = '';
  }

  // Defaults
  if (type === 'thanh_toan') {
    document.getElementById('inp-vat-pct').value = 10;
    document.getElementById('inp-giuqt-pct').value = 5;
    document.getElementById('inp-giubh-pct').value = 5;
  } else {
    document.getElementById('inp-giuqt-pct').value = 0;
    document.getElementById('inp-giubh-pct').value = 0;
    document.getElementById('inp-trutu').value = 0;
    document.getElementById('inp-trukhac').value = 0;
    if (type === 'ung_quy') document.getElementById('inp-vat-pct').value = 0;
  }
  calcPYC();
}

// ─── Cost section handlers ───
function onCSDuAnChange() {
  const da = document.getElementById('cs-duan').value;
  const filtered = da ? _state.allHD.filter(h => h.maDA === da) : _state.allHD;
  populateHDSelect('cs-hd', filtered, true);
}

async function onCSHDChange() {
  const maHD = document.getElementById('cs-hd').value;
  if (!maHD) {
    ['cs-dot', 'cs-ncc', 'cs-macp'].forEach(id => document.getElementById(id).value = '');
    document.getElementById('cs-voucher-maduan').style.display = 'none';
    document.getElementById('budget-guard').style.display = 'none';
    document.getElementById('import-box').style.display = 'none';
    document.getElementById('inp-mahd').value = '';
    document.getElementById('inp-nthhuong').value = '';
    return;
  }

  if (maHD === '__VOUCHER__') {
    document.getElementById('cs-voucher-maduan').style.display = '';
    document.getElementById('cs-ncc').value = '';
    document.getElementById('cs-ncc').removeAttribute('readonly');
    document.getElementById('cs-dot').value = '1';
    document.getElementById('inp-mahd').value = '';
    document.getElementById('import-box').style.display = 'none';
    updateMaCP();
    return;
  }

  document.getElementById('cs-voucher-maduan').style.display = 'none';
  document.getElementById('cs-ncc').setAttribute('readonly', true);

  // Get HĐ info
  const hd = _state.allHD.find(h => h.ma === maHD);
  document.getElementById('cs-ncc').value = hd?.ncc || '';
  document.getElementById('inp-mahd').value = maHD;
  document.getElementById('inp-nthhuong').value = hd?.ncc || '';

  // Calculate next đợt from chi_phi
  try {
    const { data: cpRows } = await supabase.from('chi_phi')
      .select('dot').eq('ma_hd', maHD).is('deleted_at', null);
    const dotNums = (cpRows || [])
      .map(r => parseInt(r.dot)).filter(n => !isNaN(n));
    const nextDot = dotNums.length ? Math.max(...dotNums) + 1 : 1;
    document.getElementById('cs-dot').value = String(nextDot);
    updateMaCP();
  } catch (e) {
    document.getElementById('cs-dot').value = '1';
    updateMaCP();
  }

  // Load budget guard
  try {
    const b = await checkBudget(maHD);
    _state.budget = b;
    if (b && b.ok && b.tongHD > 0) {
      document.getElementById('budget-guard').style.display = '';
      updateBudgetGuard();
    } else {
      document.getElementById('budget-guard').style.display = 'none';
    }
  } catch (e) { /* silent */ }

  // v3.1: Show import box + pre-fetch hop_dong_items
  if (hd && hd.id) {
    document.getElementById('import-box').style.display = '';
    prefetchHopDongItems(hd.id);
  } else {
    document.getElementById('import-box').style.display = 'none';
  }
}

// v3.1: Pre-fetch hạng mục HĐ (dùng cache 5 phút)
async function prefetchHopDongItems(hopDongId) {
  setImportBtnState('fetching');
  const cached = _state.itemsCache[hopDongId];
  const now = Date.now();
  if (cached && (now - cached.fetchedAt) < _state.itemsCacheTTL) {
    setImportBtnState('ready', cached.items.length);
    return;
  }
  try {
    const { data, error } = await supabase.from('hop_dong_items')
      .select('noi_dung, don_vi, so_luong, don_gia, loai_cp, sort_order')
      .eq('hop_dong_id', hopDongId)
      .order('sort_order', { nullsFirst: false });
    if (error) throw error;
    _state.itemsCache[hopDongId] = { items: data || [], fetchedAt: Date.now() };
    // Chỉ update UI nếu user vẫn đang chọn HĐ này
    const curMaHD = document.getElementById('cs-hd').value;
    const curHD = _state.allHD.find(h => h.ma === curMaHD);
    if (curHD && curHD.id === hopDongId) {
      setImportBtnState('ready', (data || []).length);
    }
  } catch (e) {
    setImportBtnState('error', 0, e.message);
  }
}

function setImportBtnState(state, count = 0, errMsg = '') {
  const btn = document.getElementById('btn-import-hd');
  const chip = document.getElementById('import-chip');
  const icon = document.getElementById('import-btn-icon');
  const label = document.getElementById('import-btn-label');
  if (!btn || !chip) return;

  if (state === 'fetching') {
    btn.disabled = true;
    btn.style.opacity = '0.6';
    btn.style.cursor = 'wait';
    icon.textContent = '⏳';
    label.textContent = 'Đang tải danh mục...';
    chip.textContent = '⏳ Đang tải...';
    chip.style.background = '#F4F4FB';
    chip.style.color = '#666';
  } else if (state === 'ready') {
    if (count > 0) {
      btn.disabled = false;
      btn.style.opacity = '1';
      btn.style.cursor = 'pointer';
      icon.textContent = '⬇';
      label.textContent = `Import ${count} hạng mục`;
      chip.textContent = `✓ Sẵn sàng (${count})`;
      chip.style.background = '#E8F5EE';
      chip.style.color = '#1B6B3A';
    } else {
      btn.disabled = true;
      btn.style.opacity = '0.6';
      btn.style.cursor = 'not-allowed';
      icon.textContent = 'ℹ';
      label.textContent = 'Không có hạng mục';
      chip.textContent = '— Trống';
      chip.style.background = '#FEF3C7';
      chip.style.color = '#854F0B';
    }
  } else if (state === 'error') {
    btn.disabled = false;
    btn.style.opacity = '1';
    btn.style.cursor = 'pointer';
    icon.textContent = '⚠';
    label.textContent = 'Thử lại';
    chip.textContent = '❌ Lỗi';
    chip.style.background = '#FEE2E2';
    chip.style.color = '#991B1B';
    chip.title = errMsg || '';
  }
}

// v3.1: Import hạng mục từ hop_dong_items vào cost table
async function importItemsFromHD() {
  const curMaHD = document.getElementById('cs-hd').value;
  const hd = _state.allHD.find(h => h.ma === curMaHD);
  if (!hd || !hd.id) { err('Chưa chọn HĐ hợp lệ'); return; }

  const cached = _state.itemsCache[hd.id];
  let items = [];
  if (cached && (Date.now() - cached.fetchedAt) < _state.itemsCacheTTL) {
    items = cached.items;
  } else {
    // Re-fetch
    setImportBtnState('fetching');
    try {
      const { data, error } = await supabase.from('hop_dong_items')
        .select('noi_dung, don_vi, so_luong, don_gia, loai_cp, sort_order')
        .eq('hop_dong_id', hd.id)
        .order('sort_order', { nullsFirst: false });
      if (error) throw error;
      items = data || [];
      _state.itemsCache[hd.id] = { items, fetchedAt: Date.now() };
      setImportBtnState('ready', items.length);
    } catch (e) {
      setImportBtnState('error', 0, e.message);
      err('Lỗi tải danh mục: ' + e.message);
      return;
    }
  }

  if (!items.length) {
    err('HĐ này không có hạng mục');
    return;
  }

  // Confirm nếu đang có dòng với dữ liệu
  const existingRows = document.querySelectorAll('#cost-tbody tr');
  const hasData = Array.from(existingRows).some(tr => {
    const nd = tr.querySelector('[name=noidung]');
    return nd && nd.value.trim();
  });
  if (hasData) {
    if (!confirm(`Import ${items.length} hạng mục sẽ xóa ${existingRows.length} dòng hiện tại. Tiếp tục?`)) return;
  }

  // Clear + import. Khối lượng để 0 để user tự nhập lại theo nghiệm thu kỳ này
  document.getElementById('cost-tbody').innerHTML = '';
  _state.rowCount = 0;
  items.forEach(it => {
    addRow({
      noi_dung: it.noi_dung || '',
      don_vi: it.don_vi || '',
      so_luong: 0,  // User nhập lại theo nghiệm thu đợt này
      don_gia: it.don_gia || 0,
      loai_cp: it.loai_cp || ''
    });
  });
  updateCostTotal();
  ok(`Đã import ${items.length} hạng mục. Hãy cập nhật khối lượng theo nghiệm thu kỳ này.`);
}

function updateMaCP() {
  const dot = document.getElementById('cs-dot').value;
  const maHD = document.getElementById('cs-hd').value;
  const macpInp = document.getElementById('cs-macp');
  if (!dot) { macpInp.value = ''; return; }
  if (maHD === '__VOUCHER__') {
    const maDA = (document.getElementById('cs-inp-maduan').value || document.getElementById('cs-duan').value || '???').trim();
    const today = new Date();
    const ymd = today.getFullYear() + String(today.getMonth() + 1).padStart(2, '0') + String(today.getDate()).padStart(2, '0');
    macpInp.value = `${dot}-VOUCHER|${ymd}|${maDA}`;
  } else if (maHD) {
    const maHDGoc = maHD.indexOf('/PL-') !== -1 ? maHD.split('/PL-')[0] : maHD;
    macpInp.value = `${dot}-${maHDGoc}`;
  }
  // Auto-generate số phiếu khi chưa có
  if (!document.getElementById('inp-sophieu').value) {
    document.getElementById('inp-sophieu').value = 'PYC-' + macpInp.value;
  }
}

function updateBudgetGuard() {
  if (!_state.budget) return;
  const b = _state.budget;
  // Include current form total
  let formTotal = 0;
  document.querySelectorAll('#cost-tbody tr').forEach(tr => {
    const inp = tr.querySelector('[name=thanhtien]');
    if (inp) formTotal += parseVND(inp.value);
  });
  const ca = Number(b.tongCP || 0) + formTotal;
  const pct = b.tongHD > 0 ? Math.round(ca / b.tongHD * 100) : 0;
  const lv = pct >= 110 ? ['#7F1D1D', 'blocked'] : pct >= 100 ? ['#DC2626', 'danger'] : pct >= 80 ? ['#D97706', 'warning'] : ['#1B6B3A', 'ok'];
  document.getElementById('bg-pct').textContent = pct + '%';
  document.getElementById('bg-pct').style.color = lv[0];
  const fill = document.getElementById('bg-fill');
  fill.style.width = Math.min(pct, 100) + '%';
  fill.style.background = lv[0];
  document.getElementById('bg-hd').textContent = formatVND(b.tongHD);
  document.getElementById('bg-cp').textContent = formatVND(ca);
  const rm = b.tongHD - ca;
  document.getElementById('bg-remain').textContent = (rm < 0 ? '-' : '') + formatVND(Math.abs(rm));
}

// ─── Tạm ứng HD change ───
function onTUHDChange() {
  const maHD = document.getElementById('tu-hd').value;
  const hd = _state.allHD.find(h => h.ma === maHD);
  document.getElementById('tu-giatri').value = hd ? formatVND(hd.giaTri) : '';
  document.getElementById('inp-mahd').value = maHD || '';
  document.getElementById('inp-nthhuong').value = hd?.ncc || '';
  if (maHD) document.getElementById('inp-sophieu').value = 'PYC-TU-' + maHD;
}

// ─── Cost row management ───
function addRow(data = {}) {
  _state.rowCount++;
  const tbody = document.getElementById('cost-tbody');
  const tr = document.createElement('tr');
  tr.id = 'cost-row-' + _state.rowCount;

  const lcpOpts = '<option value="">--</option>' +
    _state.loaiCPList.map(lcp => `<option value="${lcp}" ${data.loai_cp === lcp ? 'selected' : ''}>${lcp}</option>`).join('');
  const plOpts = ['Contract_Direct', 'Contract_Allocated', 'Obtain_Contract', 'Operating_SGA', 'Inventory', 'Prepaid']
    .map(p => `<option value="${p}" ${data.phan_loai === p ? 'selected' : ''}>${p}</option>`).join('');
  const vtOpts = '<option value="">--</option>' +
    _state.vatTuList.map(v => `<option value="${v.ma_vat_tu}" ${data.ma_vat_tu === v.ma_vat_tu ? 'selected' : ''}>${v.ma_vat_tu} — ${esc(v.ten_vat_tu)}</option>`).join('');

  tr.innerHTML = `
    <td class="num small">${_state.rowCount}</td>
    <td><input name="noidung" required value="${esc(data.noi_dung || '')}"></td>
    <td><input name="donvi" value="${esc(data.don_vi || '')}"></td>
    <td><input name="khoiluong" inputmode="numeric" value="${data.so_luong ? formatVND(data.so_luong) : ''}" style="text-align:right"></td>
    <td><input name="dongia" inputmode="numeric" value="${data.don_gia ? formatVND(data.don_gia) : ''}" style="text-align:right"></td>
    <td><input name="thanhtien" readonly class="bold" style="text-align:right;color:#1B6B3A" value="${data.thanh_tien ? formatVND(data.thanh_tien) : '0'}"></td>
    <td><select name="loaicp" required>${lcpOpts}</select></td>
    <td><select name="phanloai" style="font-size:10px">${plOpts}</select></td>
    <td><input name="sothangpb" type="number" min="1" max="60" placeholder="—" disabled style="text-align:center;font-size:11px" value="${data.so_thang_pb || ''}" title="Bắt buộc khi IFRS=Prepaid (1-60 tháng)"></td>
    <td><select name="mavattu" style="font-size:10px" disabled>${vtOpts}</select></td>
    <td><button type="button" class="btn-icon" data-del>✕</button></td>`;
  tbody.appendChild(tr);

  const inpKL = tr.querySelector('[name=khoiluong]');
  const inpDG = tr.querySelector('[name=dongia]');
  const inpTT = tr.querySelector('[name=thanhtien]');
  const selLCP = tr.querySelector('[name=loaicp]');
  const selPL = tr.querySelector('[name=phanloai]');
  const selVT = tr.querySelector('[name=mavattu]');
  const inpThangPB = tr.querySelector('[name=sothangpb]');
  attachVNDFormat(inpKL);
  attachVNDFormat(inpDG);

  // Sync số tháng PB enable/disable theo phan_loai
  const syncSoThangPB = () => {
    const isPrepaid = selPL.value === 'Prepaid';
    inpThangPB.disabled = !isPrepaid;
    if (!isPrepaid) {
      inpThangPB.value = '';
      inpThangPB.style.background = '';
    } else {
      inpThangPB.style.background = '#FAEEDA';
      if (!inpThangPB.value) inpThangPB.value = '12';
    }
  };
  selPL.addEventListener('change', syncSoThangPB);
  setTimeout(syncSoThangPB, 0); // Initial sync (after data populated)

  const calcRow = () => {
    const kl = parseVND(inpKL.value);
    const dg = parseVND(inpDG.value);
    inpTT.value = formatVND(kl * dg);
    updateCostTotal();
    updateBudgetGuard();
  };
  inpKL.addEventListener('input', calcRow);
  inpDG.addEventListener('input', calcRow);

  selLCP.addEventListener('change', () => {
    const rule = _state.ifrsRules[selLCP.value];
    if (rule) {
      selPL.value = rule.default_phan_loai;
      selPL.disabled = !!rule.locked;
    }
    syncVTState(tr);
    syncSoThangPB();
  });
  selPL.addEventListener('change', () => syncVTState(tr));

  tr.querySelector('[data-del]').addEventListener('click', () => {
    tr.remove();
    updateCostTotal();
  });

  syncVTState(tr);
  updateCostTotal();
}

function syncVTState(tr) {
  const pl = tr.querySelector('[name=phanloai]').value;
  const vt = tr.querySelector('[name=mavattu]');
  const isInv = pl === 'Inventory';
  vt.disabled = !isInv;
  vt.style.opacity = isInv ? '1' : '0.3';
  if (!isInv) vt.value = '';
}

function updateCostTotal() {
  let total = 0;
  document.querySelectorAll('#cost-tbody tr').forEach(tr => {
    total += parseVND(tr.querySelector('[name=thanhtien]').value);
  });
  document.getElementById('cost-total').textContent = formatVND(total);
  // Sync thành tiền xuống payment section
  if (_state.loaiPYC === 'thanh_toan') {
    document.getElementById('inp-thanhtien').value = total;
    calcPYC();
  }
}

// ─── Payment calc ───
function calcPYC() {
  const tt = parseFloat(document.getElementById('inp-thanhtien').value) || 0;
  let vat = 0, sauVat = tt, giuQT = 0, giuBH = 0, truTU = 0, truKhac = 0, chiTrongKy = tt;

  if (_state.loaiPYC === 'thanh_toan') {
    const vatPct = parseFloat(document.getElementById('inp-vat-pct').value) || 0;
    const giuQTPct = parseFloat(document.getElementById('inp-giuqt-pct').value) || 0;
    const giuBHPct = parseFloat(document.getElementById('inp-giubh-pct').value) || 0;
    truTU = parseFloat(document.getElementById('inp-trutu').value) || 0;
    truKhac = parseFloat(document.getElementById('inp-trukhac').value) || 0;
    vat = Math.round(tt * vatPct / 100);
    sauVat = tt + vat;
    giuQT = Math.round(sauVat * giuQTPct / 100);
    giuBH = Math.round(sauVat * giuBHPct / 100);
    chiTrongKy = sauVat - giuQT - giuBH - truTU - truKhac;
  } else if (_state.loaiPYC === 'tam_ung') {
    const vatPct = parseFloat(document.getElementById('inp-vat-pct').value) || 0;
    vat = Math.round(tt * vatPct / 100);
    sauVat = tt + vat;
    chiTrongKy = sauVat;
  }

  _state.calc = { vat, sauVat, giuQT, giuBH, truTU, truKhac, chiTrongKy };

  document.getElementById('d-thanhtien').textContent = formatVND(tt);
  document.getElementById('d-vat').textContent = formatVND(vat);
  document.getElementById('d-sauvat').textContent = formatVND(sauVat);
  document.getElementById('d-giuqt').textContent = formatVND(giuQT);
  document.getElementById('d-giubh').textContent = formatVND(giuBH);
  const elCTK = document.getElementById('d-chitrongky');
  if (chiTrongKy < 0) {
    elCTK.textContent = formatVND(chiTrongKy) + ' ⚠';
    elCTK.style.color = '#A8000A';
  } else {
    elCTK.textContent = formatVND(chiTrongKy);
    elCTK.style.color = '#1B6B3A';
  }
  checkCashWarning();
}

function checkCashWarning() {
  const w = document.getElementById('cash-warn');
  const c = _state.calc.chiTrongKy || 0;
  if (_state.hinhThucTT === 'tien_mat' && c > _state.cashThreshold) {
    w.style.display = 'block';
    w.textContent = `⚠ Chi trong kỳ (${formatVND(c)} VNĐ) vượt ngưỡng thanh toán tiền mặt (${formatVND(_state.cashThreshold)} VNĐ). Khuyến nghị dùng chuyển khoản.`;
  } else {
    w.style.display = 'none';
  }
}

function setHTTT(v) {
  _state.hinhThucTT = v;
  ['tk', 'tm'].forEach(k => {
    const el = document.getElementById('httt-' + k);
    if (el) {
      const active = (k === 'tk' && v === 'tai_khoan') || (k === 'tm' && v === 'tien_mat');
      el.style.background = active ? '#000066' : '#FFF';
      el.style.color = active ? '#FFCC00' : '#666';
      el.style.border = active ? '2px solid #000066' : '2px solid #DDD';
    }
  });
  checkCashWarning();
}

// ─── Save ───
function _getCostRows() {
  const rows = [];
  document.querySelectorAll('#cost-tbody tr').forEach(tr => {
    const nd = tr.querySelector('[name=noidung]').value.trim();
    if (!nd) return;
    const phanLoai = tr.querySelector('[name=phanloai]').value;
    const stpVal = tr.querySelector('[name=sothangpb]').value;
    rows.push({
      noi_dung: nd,
      don_vi: tr.querySelector('[name=donvi]').value || null,
      so_luong: parseVND(tr.querySelector('[name=khoiluong]').value),
      don_gia: parseVND(tr.querySelector('[name=dongia]').value),
      thanh_tien: parseVND(tr.querySelector('[name=thanhtien]').value),
      loai_cp: tr.querySelector('[name=loaicp]').value,
      phan_loai: phanLoai,
      ma_vat_tu: tr.querySelector('[name=mavattu]').value || null,
      so_thang_pb: (phanLoai === 'Prepaid' && stpVal) ? parseInt(stpVal, 10) : null
    });
  });
  return rows;
}

async function savePYC(submitAfter) {
  const btn = submitAfter ? document.getElementById('btn-submit') : document.getElementById('btn-save');
  const typeLabel = { thanh_toan: 'Thanh toán', tam_ung: 'Tạm ứng', ung_quy: 'Ứng quỹ' }[_state.loaiPYC];

  // Common validation
  const kytt = document.getElementById('inp-kytt').value.trim();
  if (!kytt) { err('Thiếu Kỳ thanh toán'); return; }
  if (_state.calc.chiTrongKy < 0) { err('Chi trong kỳ âm — kiểm tra lại'); return; }

  let maHD = '', costRows = [], maDuAn = '', sophieu = document.getElementById('inp-sophieu').value || '';
  let nthhuong = document.getElementById('inp-nthhuong').value.trim();

  // Type-specific validation + data extraction
  if (_state.loaiPYC === 'thanh_toan') {
    maHD = document.getElementById('cs-hd').value;
    if (!maHD) { err('Chưa chọn HĐ'); return; }
    if (maHD === '__VOUCHER__') {
      maDuAn = (document.getElementById('cs-inp-maduan').value || '').trim();
      if (!maDuAn) { err('Thiếu Mã dự án cho Voucher'); return; }
      maHD = ''; // voucher không có ma_hd thực
    } else {
      maDuAn = document.getElementById('cs-duan').value || '';
    }
    costRows = _getCostRows();
    if (!costRows.length) { err('Chưa có dòng chi phí nào'); return; }
    const totalCheck = costRows.reduce((s, r) => s + (r.thanh_tien || 0), 0);
    if (totalCheck <= 0) { err('Tổng thành tiền = 0'); return; }
    // Budget check
    if (_state.budget && _state.budget.tongHD > 0) {
      const bgAfter = Number(_state.budget.tongCP || 0) + totalCheck;
      const bgPct = Math.round(bgAfter / _state.budget.tongHD * 100);
      if (bgPct > 110) {
        err(`VƯỢT NGÂN SÁCH! Chi phí lũy kế (${formatVND(bgAfter)}) vượt 110% HĐ. Đã chặn lưu.`);
        return;
      }
      if (bgPct >= 80) {
        if (!confirm(`⚠ Cảnh báo ngân sách: ${bgPct}%. Tiếp tục lưu?`)) return;
      }
    }
    // Validate Inventory → bắt buộc Mã VT
    const missingVT = costRows.filter(r => r.phan_loai === 'Inventory' && !r.ma_vat_tu);
    if (missingVT.length) { err(`${missingVT.length} dòng Inventory thiếu Mã VT`); return; }
    // Validate Prepaid → bắt buộc số tháng PB (1-60)
    const badPrepaid = costRows.filter(r => r.phan_loai === 'Prepaid' && (!r.so_thang_pb || r.so_thang_pb < 1 || r.so_thang_pb > 60));
    if (badPrepaid.length) { err(`${badPrepaid.length} dòng Prepaid thiếu/sai số tháng phân bổ (cần 1-60)`); return; }
  } else if (_state.loaiPYC === 'tam_ung') {
    maHD = document.getElementById('tu-hd').value;
    if (!maHD) { err('Chưa chọn HĐ cho tạm ứng'); return; }
    maDuAn = document.getElementById('tu-duan').value || '';
    const tt = parseFloat(document.getElementById('inp-thanhtien').value) || 0;
    if (tt <= 0) { err('Số tiền tạm ứng = 0'); return; }
  } else if (_state.loaiPYC === 'ung_quy') {
    const nd = document.getElementById('uq-noidung').value.trim();
    if (!nd) { err('Chưa nhập nội dung ứng quỹ'); return; }
    maHD = document.getElementById('uq-hd').value || null;
    maDuAn = document.getElementById('uq-duan').value || '';
    nthhuong = nthhuong || nd;
    const tt = parseFloat(document.getElementById('inp-thanhtien').value) || 0;
    if (tt <= 0) { err('Số tiền ứng quỹ = 0'); return; }
  }

  if (!nthhuong) nthhuong = 'N/A';

  const tt = parseFloat(document.getElementById('inp-thanhtien').value) || 0;
  const c = _state.calc;

  const pycPayload = {
    so_pyctt: sophieu || null,       // trigger fill nếu null
    loai_pyc: _state.loaiPYC,
    ma_hd: maHD || null,
    ma_du_an: maDuAn || null,
    nguoi_thu_huong: nthhuong,
    ky_tt: kytt,
    so_tien: tt,
    thanh_tien: tt,
    vat: c.vat || 0,
    sau_vat: c.sauVat || tt,
    giu_quyet_toan: c.giuQT || 0,
    giu_bao_hanh: c.giuBH || 0,
    tru_tam_ung: c.truTU || 0,
    tru_khac: c.truKhac || 0,
    chi_trong_ky: c.chiTrongKy || tt,
    hinh_thuc_tt: _state.hinhThucTT,
    trang_thai: 'draft'
  };

  btn.disabled = true;
  btn.textContent = 'Đang lưu...';

  try {
    let savedId = _state.editId;
    let savedSoPyctt = sophieu;

    if (_state.editId) {
      const { error: uErr } = await supabase.from('pyctt').update(pycPayload).eq('id', _state.editId);
      if (uErr) throw uErr;
      // Delete old cost rows linked to this so_pyctt
      if (_state.loaiPYC === 'thanh_toan' && savedSoPyctt) {
        await supabase.from('chi_phi').delete().eq('ma_chi_tiet', savedSoPyctt).eq('trang_thai', 'draft');
      }
    } else {
      const { data: newPYC, error: cErr } = await supabase.from('pyctt').insert(pycPayload).select('id, so_pyctt').single();
      if (cErr) throw cErr;
      savedId = newPYC.id;
      savedSoPyctt = newPYC.so_pyctt;
    }

    // Insert cost rows (only for thanh_toan)
    if (_state.loaiPYC === 'thanh_toan' && costRows.length) {
      const dot = document.getElementById('cs-dot').value || '1';
      const ncc = document.getElementById('cs-ncc').value || null;
      const cpPayload = costRows.map(r => ({
        ngay_ct: new Date().toISOString().slice(0, 10),
        ma_hd: maHD || null,
        ma_du_an: maDuAn || null,
        dot: dot,
        nha_thau: ncc,
        ten_cp: r.noi_dung,
        so_luong: r.so_luong,
        don_gia: r.don_gia,
        loai_cp: r.loai_cp,
        phan_loai: r.phan_loai,
        ma_vat_tu: r.ma_vat_tu,
        so_thang_pb: r.so_thang_pb,
        ma_chi_tiet: savedSoPyctt,  // link CP ↔ PYC
        ky_chi_phi: new Date().toISOString().slice(0, 7),
        trang_thai: 'draft'
        // thanh_tien, tong_tt: filled by triggers
        // so_ct: filled by chi_phi_resolve_fk trigger
      }));
      const { error: cpErr } = await supabase.from('chi_phi').insert(cpPayload);
      if (cpErr) throw cpErr;
    }

    if (submitAfter) {
      await submitForApproval('pyctt', savedId);
      ok('Đã lưu & gửi duyệt: ' + (savedSoPyctt || savedId));
    } else {
      ok('Đã lưu nháp: ' + (savedSoPyctt || savedId));
    }
    setTimeout(() => { window.location.hash = '#/pyctt'; }, 600);
  } catch (e) {
    err('Lỗi: ' + e.message);
    btn.disabled = false;
    btn.textContent = submitAfter ? 'Lưu & Gửi duyệt' : 'Lưu nháp';
  }
}

// ─── Edit mode loader ───
async function loadEditData(id) {
  try {
    const { data: pyc, error } = await supabase.from('pyctt').select('*').eq('id', id).maybeSingle();
    if (error || !pyc) { err('Không tìm thấy phiếu: ' + (error?.message || id)); return; }

    _state.editData = pyc;
    switchType(pyc.loai_pyc || 'thanh_toan');
    // Lock type bar in edit mode
    document.getElementById('pyc-type-bar').style.pointerEvents = 'none';
    document.getElementById('pyc-type-bar').style.opacity = '0.7';

    document.getElementById('inp-sophieu').value = pyc.so_pyctt || '';
    document.getElementById('inp-mahd').value = pyc.ma_hd || '';
    document.getElementById('inp-nthhuong').value = pyc.nguoi_thu_huong || '';
    document.getElementById('inp-kytt').value = pyc.ky_tt || '';
    document.getElementById('inp-thanhtien').value = Number(pyc.thanh_tien || pyc.so_tien || 0);

    // Derive percentages
    const tt = Number(pyc.thanh_tien || pyc.so_tien || 0);
    if (tt > 0) {
      document.getElementById('inp-vat-pct').value = Math.round(Number(pyc.vat || 0) / tt * 100);
      const sv = Number(pyc.sau_vat || tt);
      document.getElementById('inp-giuqt-pct').value = sv > 0 ? Math.round(Number(pyc.giu_quyet_toan || 0) / sv * 100) : 0;
      document.getElementById('inp-giubh-pct').value = sv > 0 ? Math.round(Number(pyc.giu_bao_hanh || 0) / sv * 100) : 0;
    }
    document.getElementById('inp-trutu').value = Number(pyc.tru_tam_ung || 0);
    document.getElementById('inp-trukhac').value = Number(pyc.tru_khac || 0);

    if (pyc.hinh_thuc_tt) setHTTT(pyc.hinh_thuc_tt);

    // Load cost rows if thanh_toan
    if (pyc.loai_pyc === 'thanh_toan' && pyc.so_pyctt) {
      document.getElementById('cs-hd').value = pyc.ma_hd || '';
      const { data: costs } = await supabase.from('chi_phi')
        .select('*').eq('ma_chi_tiet', pyc.so_pyctt).is('deleted_at', null).order('created_at');
      if (costs && costs.length) {
        document.getElementById('cost-tbody').innerHTML = '';
        _state.rowCount = 0;
        costs.forEach(c => addRow({
          noi_dung: c.ten_cp,
          don_vi: c.don_vi || '',
          so_luong: c.so_luong,
          don_gia: c.don_gia,
          thanh_tien: c.thanh_tien,
          loai_cp: c.loai_cp,
          phan_loai: c.phan_loai,
          ma_vat_tu: c.ma_vat_tu,
          so_thang_pb: c.so_thang_pb
        }));
      } else {
        addRow(); addRow();
      }
      if (pyc.ma_hd) {
        const hd = _state.allHD.find(h => h.ma === pyc.ma_hd);
        document.getElementById('cs-ncc').value = hd?.ncc || '';
      }
    }

    // Lock form if not editable
    if (!['draft', 'rejected'].includes(pyc.trang_thai)) {
      document.querySelectorAll('input, select, button').forEach(el => {
        if (!el.closest('.page-header')) el.disabled = true;
      });
    }

    calcPYC();
  } catch (e) {
    err('Lỗi load dữ liệu: ' + e.message);
  }
}
